import {
  DeleteOutlined,
  LeftOutlined,
  MoreOutlined,
  PlusOutlined,
  SettingOutlined
} from "@ant-design/icons"
import {
  App,
  Button,
  Dropdown,
  Flex,
  Result,
  Space,
  Tag,
  Typography
} from "antd"
import type { TableColumnsType } from "antd"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CardComponent } from "../../components/Card/CardComponent"
import { EntityTable } from "../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../components/EntityTable/surfaces.ts"
import { SearchBar } from "../../components/Input/SearchBar"
import { generateDocumentTitle } from "../../constants/document-title"
import { USER_ROLE_OPTIONS } from "../../constants/roles"
import { RoutesPaths } from "../../constants/routes"
import { getDashboardCatalog } from "../DashboardPage/api/services"
import { specToInstance } from "../DashboardPage/components/Portfolio/widgetsResponseAdapter"
import type { WidgetCatalog } from "../DashboardPage/redux/widgets.types"
import {
  createDashboardTemplate,
  deleteDashboardTemplate,
  getDashboardTemplates,
  getTemplateRoles,
  updateDashboardTemplate
} from "./api/services"
import {
  type AvailableWidget,
  TemplateEditor
} from "./components/TemplateEditor"
import {
  type TemplateFormValues,
  asTemplateList,
  buildTemplatePayload,
  unwrapEnvelope
} from "./helpers/payload"
import type { DashboardTemplate, TemplateRole } from "./types"

const { Text, Title } = Typography

export default function DashboardTemplatesSettings() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()

  const [available, setAvailable] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [templates, setTemplates] = useState<DashboardTemplate[]>([])
  const [catalog, setCatalog] = useState<WidgetCatalog | null>(null)
  const [audienceRole, setAudienceRole] = useState<number | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [authorableRoles, setAuthorableRoles] = useState<number[] | null>(null)

  // Labels stay client-side (translated); the backend decides which roles a
  // template can be authored for, since it owns the catalogs.
  const roles = useMemo<TemplateRole[]>(
    () =>
      USER_ROLE_OPTIONS.filter(
        (r) => authorableRoles?.includes(r.id) ?? true
      ).map((r) => ({
        id: r.id,
        name: t(r.nameKey, r.fallback)
      })),
    [t, authorableRoles]
  )

  const roleLabels = useMemo<TemplateRole[]>(
    () =>
      USER_ROLE_OPTIONS.map((r) => ({
        id: r.id,
        name: t(r.nameKey, r.fallback)
      })),
    [t]
  )

  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<DashboardTemplate | null>(null)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState<string | null>(null)

  const loadTemplates = useCallback(async () => {
    const res = await getDashboardTemplates()
    if (res.status === 404) {
      setAvailable(false)
      return
    }
    setAvailable(true)
    if (res.ok) setTemplates(asTemplateList(res.data))
  }, [])

  useEffect(() => {
    generateDocumentTitle("Dashboard Templates")
    let active = true
    void (async () => {
      try {
        await loadTemplates()
        const rolesRes = await getTemplateRoles()
        if (!active) return
        if (rolesRes.ok) {
          setAuthorableRoles(
            unwrapEnvelope<{ roles: number[] }>(rolesRes.data)?.roles ?? null
          )
        }
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [loadTemplates])

  useEffect(() => {
    let active = true
    setCatalogLoading(true)
    void (async () => {
      try {
        const res = await getDashboardCatalog(audienceRole)
        if (!active) return
        if (res.ok) setCatalog(unwrapEnvelope<WidgetCatalog>(res.data) ?? null)
      } finally {
        if (active) setCatalogLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [audienceRole])

  const availableWidgets = useMemo<AvailableWidget[]>(
    () =>
      (catalog?.widgets ?? []).map((spec) => ({
        instance: specToInstance(spec),
        title: `${spec.title} (${spec.type})`
      })),
    [catalog]
  )

  const restrictedTitles = useMemo(() => {
    const map = new Map<string, string>()
    for (const spec of catalog?.restricted_widgets ?? []) {
      map.set(specToInstance(spec).slug, `${spec.title} (${spec.type})`)
    }
    return map
  }, [catalog])

  const roleLabel = useCallback(
    (id: number | null) =>
      roleLabels.find((r) => r.id === id)?.name ??
      (id != null ? String(id) : "—"),
    [roleLabels]
  )

  // The endpoint returns every template at once and the table does not paginate,
  // so the search narrows the rows in place instead of round-tripping.
  const visible = useMemo(() => {
    const needle = search?.trim().toLowerCase()
    if (!needle) return templates
    return templates.filter((row) =>
      [
        row.layout.name,
        row.key,
        row.scope === "role" ? roleLabel(row.role) : ""
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    )
  }, [templates, search, roleLabel])

  const openCreate = () => {
    setEditing(null)
    setEditorOpen(true)
  }

  const openEdit = (template: DashboardTemplate) => {
    setEditing(template)
    setEditorOpen(true)
  }

  const handleSubmit = async (values: TemplateFormValues) => {
    setSaving(true)
    try {
      const payload = buildTemplatePayload(values)
      const res = editing
        ? await updateDashboardTemplate(editing.id, payload)
        : await createDashboardTemplate(payload)
      if (!res.ok) {
        void message.error(
          t("settingsDashboardTemplates.saveFailed", "Failed to save template")
        )
        return
      }
      setEditorOpen(false)
      setEditing(null)
      void message.success(
        t("settingsDashboardTemplates.saved", "Template saved")
      )
      await loadTemplates()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number | string) => {
    const res = await deleteDashboardTemplate(id)
    if (!res.ok) {
      void message.error(
        t(
          "settingsDashboardTemplates.deleteFailed",
          "Failed to delete template"
        )
      )
      return
    }
    void message.success(
      t("settingsDashboardTemplates.deleted", "Template deleted")
    )
    await loadTemplates()
  }

  const confirmDelete = (row: DashboardTemplate) => {
    modal.confirm({
      centered: true,
      title: t(
        "settingsDashboardTemplates.deleteConfirm",
        "Delete this template?"
      ),
      okText: t("settingsDashboardTemplates.delete", "Delete"),
      cancelText: t("settingsDashboardTemplates.editor.cancel", "Cancel"),
      okButtonProps: { danger: true },
      onOk: () => handleDelete(row.id)
    })
  }

  const columns: TableColumnsType<DashboardTemplate> = [
    {
      key: "scope",
      title: t("settingsDashboardTemplates.column.scope", "Scope"),
      render: (_, row) =>
        row.scope === "role"
          ? t("settingsDashboardTemplates.scope.role", "Role")
          : t("settingsDashboardTemplates.scope.company", "Company")
    },
    {
      key: "role",
      title: t("settingsDashboardTemplates.column.role", "Role"),
      render: (_, row) => (row.scope === "role" ? roleLabel(row.role) : "—")
    },
    {
      key: "key",
      title: t("settingsDashboardTemplates.column.key", "Key"),
      render: (_, row) => row.key
    },
    {
      key: "name",
      title: t("settingsDashboardTemplates.column.name", "Name"),
      render: (_, row) => row.layout.name
    },
    {
      key: "widgets",
      title: t("settingsDashboardTemplates.column.widgets", "Widgets"),
      render: (_, row) => row.layout.widgets.length
    },
    {
      key: "is_default",
      title: t("settingsDashboardTemplates.column.default", "Default"),
      render: (_, row) =>
        row.is_default ? (
          <Tag color="blue">
            {t("settingsDashboardTemplates.default", "Default")}
          </Tag>
        ) : (
          "—"
        )
    },
    // Deliberately keyless: `applyConfig` treats a column with no key as an
    // always-visible passthrough pinned to the end, which is what keeps this one
    // out of the saved layout and out of reach of the reorder handles.
    //
    // One-way and unmigrated by choice. A layout saved while the column still
    // had a key keeps an entry `applyConfig` now finds no column for and drops,
    // so anyone who had hidden or reordered Actions gets it back, pinned right —
    // which is the only place it should ever have been.
    {
      title: t("settingsDashboardTemplates.column.actions", "Actions"),
      className: "text-center",
      width: 85,
      fixed: "right",
      render: (_, row) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: t("settingsDashboardTemplates.edit", "Edit"),
                icon: <SettingOutlined />,
                onClick: () => openEdit(row)
              },
              {
                key: "delete",
                label: t("settingsDashboardTemplates.delete", "Delete"),
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => confirmDelete(row)
              }
            ]
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button icon={<MoreOutlined className={"text-md"} />} />
        </Dropdown>
      )
    }
  ]

  if (available === false) {
    return (
      <div className={"grid size-full"}>
        <Flex className={"overflow-auto"} gap={"small"} vertical>
          <Result
            status="404"
            title={t(
              "settingsDashboardTemplates.unavailable.title",
              "Not available"
            )}
            subTitle={t(
              "settingsDashboardTemplates.unavailable.subtitle",
              "Dashboard templates are not enabled for this workspace."
            )}
          />
        </Flex>
      </div>
    )
  }

  return (
    <div className={"grid size-full"}>
      <Flex className={"overflow-auto"} gap={"middle"} vertical>
        <Flex align="flex-start" justify="space-between">
          <Space direction="vertical" size={2}>
            <Space>
              <Link to={RoutesPaths.settings.templates}>
                <Button icon={<LeftOutlined />} size="small" />
              </Link>
              <Title level={2} className="m-0 text-start">
                {t("settingsDashboardTemplates.title", "Dashboard Templates")}
              </Title>
            </Space>
            <Text type="secondary">
              {t(
                "settingsDashboardTemplates.subtitle",
                "Define default dashboard layouts by company or role"
              )}
            </Text>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t("settingsDashboardTemplates.create", "Template")}
          </Button>
        </Flex>

        {/* The Customize button stays hidden here — EAD-267's review took that
            call for the settings tables. The card is a separate matter: this was
            the one list in the product still sitting on the bare page. */}
        <CardComponent classNames={{ body: "p-0" }}>
          <Flex
            wrap="wrap"
            gap="small"
            className="py-4 md:p-4 border-b border-gray-200"
          >
            <div className="flex-1 max-md:basis-60 max-md:grow">
              <SearchBar
                placeholder={t(
                  "settingsDashboardTemplates.searchPlaceholder",
                  "Find template..."
                )}
                onSearch={setSearch}
              />
            </div>
          </Flex>
          <EntityTable
            surface={TABLE_SURFACES.dashboardTemplates}
            selectionMode="on-demand"
            tab={DEFAULT_TAB}
            hideConfigButton
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={visible}
            // The endpoint returns every template, so antd pages the rows it
            // already holds. `total` is what blanks the controls on a single
            // page — the wrapper's guard reads it off this object, not off
            // antd's own tally of the data source.
            pagination={{ pageSize: 20, total: visible.length }}
          />
        </CardComponent>

        <TemplateEditor
          open={editorOpen}
          editing={editing}
          availableWidgets={availableWidgets}
          restrictedTitles={restrictedTitles}
          catalogLoading={catalogLoading}
          roles={roles}
          saving={saving}
          onAudienceChange={setAudienceRole}
          onCancel={() => {
            setEditorOpen(false)
            setEditing(null)
          }}
          onSubmit={handleSubmit}
        />
      </Flex>
    </div>
  )
}
